/**
 * Hydration-payload slimming helpers.
 *
 * Every row a loader returns is serialised twice: once as rendered HTML and
 * once as the router's hydration script. On spec/attribute pages that second
 * copy was the single largest part of the document (~44 KB of a 140 KB page),
 * which crushed the text-to-HTML ratio that crawlers and site auditors use as
 * a quality signal. These helpers drop keys that carry no information (nulls)
 * and collapse rows that are byte-identical apart from their primary key.
 */

/** Remove null/undefined values so they are not serialised into the payload. */
export function slimRow<T extends Record<string, unknown>>(row: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) {
    if (v !== null && v !== undefined) out[k] = v;
  }
  return out as T;
}

export function slimRows<T extends Record<string, unknown>>(rows: T[]): T[] {
  return rows.map(slimRow);
}

/**
 * Collapse rows that describe the same vehicle configuration. The EPA feed
 * frequently ships several identical trim rows per model-year; they add HTML
 * weight and repeat the same numbers to the reader without adding meaning.
 * Comparison ignores volatile/opaque columns (`ignore`).
 */
export function dedupeRows<T extends Record<string, unknown>>(
  rows: T[],
  ignore: string[] = ["id", "verified_at", "created_at", "updated_at", "model_id"],
): T[] {
  const skip = new Set(ignore);
  const seen = new Set<string>();
  const out: T[] = [];
  for (const row of rows) {
    const key = JSON.stringify(
      Object.keys(row)
        .filter((k) => !skip.has(k))
        .sort()
        .map((k) => [k, row[k]]),
    );
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

/** Dedupe then strip nulls — the standard treatment for loader row arrays. */
export function compactRows<T extends Record<string, unknown>>(rows: T[], ignore?: string[]): T[] {
  return slimRows(dedupeRows(rows, ignore));
}

/** Keep only the listed keys (used for narrow projections of off-topic rows). */
export function pickFields<T extends Record<string, unknown>>(row: T, keys: string[]): T {
  const out: Record<string, unknown> = {};
  for (const k of keys) if (row[k] !== null && row[k] !== undefined) out[k] = row[k];
  return out as T;
}

/**
 * Provenance columns. Every row of a dataset carries the same source, source
 * URL, confidence grade and verification date, and the page renders them once
 * (the "verified" badge and the table caption). Repeating them on every row
 * costs ~4 KB of hydration payload per page and tells the reader nothing new,
 * so the loader lifts them into a single object and strips them from the rows.
 */
export const PROVENANCE_KEYS = ["source", "source_url", "confidence", "verified_at"] as const;

export interface Provenance {
  source?: string | null;
  source_url?: string | null;
  verified_at?: string | null;
}

/** Lift the provenance columns off a representative row. */
export function takeProvenance(row: Record<string, unknown> | undefined | null): Provenance {
  if (!row) return {};
  return {
    source: (row["source"] as string | null) ?? null,
    source_url: (row["source_url"] as string | null) ?? null,
    verified_at: (row["verified_at"] as string | null) ?? null,
  };
}

/** Drop the provenance columns from every row. */
export function stripProvenance<T extends Record<string, unknown>>(rows: T[]): T[] {
  return rows.map((row) => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(row)) {
      if (!(PROVENANCE_KEYS as readonly string[]).includes(k)) out[k] = v;
    }
    return out as T;
  });
}

/**
 * Replace 36-character UUID primary keys with short, stable row keys. The
 * client only ever uses `id` as a React list key, so the UUID is ~2.5 KB of
 * dead weight in the hydration payload of a typical multi-year table.
 */
export function shortIds<T extends Record<string, unknown>>(rows: T[], prefix: string): T[] {
  return rows.map((row, i) => (row["id"] === undefined ? row : { ...row, id: `${prefix}${i}` }));
}

/**
 * Keep only the newest `n` rows of an off-topic dataset.
 *
 * A Silverado bed-dimensions page shipped 201 EPA rows and 369 NHTSA dimension
 * rows in its hydration payload even though neither table is rendered there —
 * they only feed counts, chips and a couple of derived sentences. Capping them
 * to the most recent slice keeps every derived figure meaningful while cutting
 * tens of KB of invisible markup that crushes the text-to-HTML ratio.
 */
export function capRows<T extends Record<string, unknown>>(rows: T[], n: number): T[] {
  if (rows.length <= n) return rows;
  return [...rows]
    .sort((a, b) => (Number(b["year"]) || 0) - (Number(a["year"]) || 0))
    .slice(0, n);
}

/**
 * Year-balanced cap: keep up to `perYear` rows for EVERY model year rather than
 * the most recent N rows overall. The maintenance page derives its year selector
 * (and the internal links to each ?year= URL) from the rows it receives, so a
 * flat cap silently orphaned older model years.
 */
export function capRowsPerYear<T extends Record<string, unknown>>(
  rows: T[],
  perYear: number,
  max: number,
): T[] {
  const seen = new Map<number, number>();
  const out: T[] = [];
  for (const row of [...rows].sort((a, b) => (Number(b["year"]) || 0) - (Number(a["year"]) || 0))) {
    const year = Number(row["year"]) || 0;
    const count = seen.get(year) ?? 0;
    if (count >= perYear) continue;
    seen.set(year, count + 1);
    out.push(row);
    if (out.length >= max) break;
  }
  return out;
}
